import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Check, X, ChevronRight, Brain, BookOpen, AlertCircle, RefreshCw, LogOut } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { isFillBlankCorrect, normalizeAnswer } from '../utils/fillBlankChecker';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function QuizSession() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Quiz configuration from URL params (multi-select support)
  // Support both old single params (topic, difficulty) and new multi params (topics, difficulties)
  const topics = searchParams.get('topics') || searchParams.get('topic') || '';
  const difficulties = searchParams.get('difficulties') || searchParams.get('difficulty') || '';
  const subtopics = searchParams.get('subtopics') || '';
  const mode = searchParams.get('mode') || 'standard';
  
  // Quiz state
  const [questions, setQuestions] = useState([]);
  const [meta, setMeta] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentFeedback, setCurrentFeedback] = useState(null);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Session progress tracking
  const [sessionStats, setSessionStats] = useState({
    questionsAnswered: 0,
    correctCount: 0,
    totalTimeSpent: 0
  });
  
  // Exit confirmation modal state
  const [showExitModal, setShowExitModal] = useState(false);

  const fetchQuiz = useCallback(async () => {
    try {
      const params = {
        mode,
        limit: 10
      };
      
      // Add topics filter if specified
      if (topics && topics !== 'all') {
        params.topics = topics;
      }
      
      // Add difficulties filter if specified
      if (difficulties && difficulties !== 'all') {
        params.difficulties = difficulties;
      }
      
      // Add subtopics filter if specified
      if (subtopics) {
        params.subtopics = subtopics;
      }
      
      const response = await axios.get(`${BACKEND_URL}/api/quiz/questions`, {
        params,
        withCredentials: true
      });
      
      setQuestions(response.data.questions);
      setMeta(response.data.meta);
      setQuestionStartTime(Date.now());
      
      if (response.data.questions.length === 0) {
        toast.error('No questions available for this selection');
      }
    } catch (error) {
      toast.error('Failed to load quiz');
      navigate('/quiz/setup');
    } finally {
      setLoading(false);
    }
  }, [topics, difficulties, subtopics, mode, navigate]);

  useEffect(() => {
    fetchQuiz();
  }, [fetchQuiz]);

  // ============================================
  // UNIFIED EXIT HANDLER - Always exits reliably
  // ============================================
  
  // Perform the actual exit
  const performExit = useCallback(() => {
    // Reset all quiz state before navigating
    setQuestions([]);
    setCurrentIndex(0);
    setAnswers({});
    setShowFeedback(false);
    setCurrentFeedback(null);
    setSessionStats({ questionsAnswered: 0, correctCount: 0, totalTimeSpent: 0 });
    setShowExitModal(false);
    
    // Navigate deterministically
    navigate('/quiz/setup', { replace: true });
  }, [navigate]);

  const handleExitQuiz = useCallback(() => {
    const isInProgress = currentIndex > 0 || Object.keys(answers).length > 0;
    
    if (isInProgress) {
      // Show confirmation modal instead of window.confirm
      setShowExitModal(true);
      return;
    }
    
    // If quiz hasn't started, exit immediately
    performExit();
  }, [currentIndex, answers, performExit]);

  const handleAnswer = (answer) => {
    if (showFeedback) return; // Don't allow changing answer after feedback shown
    
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    const currentQuestion = questions[currentIndex];
    
    if (!currentQuestion) return;
    
    setAnswers({
      ...answers,
      [currentQuestion.question_id]: {
        answer,
        time_spent: timeSpent
      }
    });
  };

  // ============================================
  // SAFE CHECK ANSWER - Handles all null/undefined cases
  // ============================================
  const handleCheckAnswer = async () => {
    const currentQuestion = questions[currentIndex];
    
    // Guard: Check question exists
    if (!currentQuestion) {
      toast.error('Question not found');
      return;
    }
    
    const userAnswerData = answers[currentQuestion.question_id];
    const userAnswer = userAnswerData?.answer;
    
    // Guard: Check user provided an answer
    if (!userAnswer && userAnswer !== '') {
      toast.error('Please select an answer');
      return;
    }
    
    // =====================================================
    // SAFE ANSWER COMPARISON - Never crashes on null/undefined
    // =====================================================
    let isCorrect = false;
    let correctAnswerDisplay = '';
    
    // Get correct answer safely - handle null, undefined, numbers, etc.
    const rawCorrectAnswer = currentQuestion.correct_answer;
    const correctAnswerText = currentQuestion.correct_answer_text; // For fill-in-blank
    
    // Determine the expected answer based on question type
    let expected = '';
    if (currentQuestion.question_type === 'fill_in_blank') {
      // Fill-in-blank: Check correct_answer_text first, then correct_answer
      expected = (correctAnswerText ?? rawCorrectAnswer ?? '').toString().trim();
    } else {
      expected = (rawCorrectAnswer ?? '').toString().trim();
    }
    
    // Normalize user answer
    const actual = (userAnswer ?? '').toString().trim();
    
    // Handle missing correct answer gracefully
    if (!expected) {
      toast.error('This question is missing an answer key. Marked as incorrect.');
      isCorrect = false;
      correctAnswerDisplay = 'Answer key missing';
    } else {
      // Perform comparison based on question type
      if (currentQuestion.question_type === 'fill_in_blank') {
        // Fill-in-blank: Use enhanced isFillBlankCorrect with flexible matching
        // Support multiple acceptable answers from question data
        const acceptableAnswers = currentQuestion.acceptable_answers || [];
        
        // Also support pipe-separated answers in correct_answer field
        const pipeAnswers = expected.split('|').map(a => a.trim());
        const allAcceptable = [...pipeAnswers.slice(1), ...acceptableAnswers];
        
        // Use new isFillBlankCorrect function with:
        // - Normalization (trim, lowercase, remove punctuation)
        // - Contains matching
        // - Token overlap (70% threshold)
        // - Auto-derived acceptable answers from patterns like "VA (Veno-Arterial)"
        isCorrect = isFillBlankCorrect(actual, pipeAnswers[0], allAcceptable);
        correctAnswerDisplay = pipeAnswers[0]; // Show primary correct answer
      } else {
        // MCQ / True-False: Direct comparison (strict match)
        isCorrect = normalizeAnswer(actual) === normalizeAnswer(expected);
        correctAnswerDisplay = expected;
      }
    }
    
    // Update session stats
    setSessionStats(prev => ({
      questionsAnswered: prev.questionsAnswered + 1,
      correctCount: prev.correctCount + (isCorrect ? 1 : 0),
      totalTimeSpent: prev.totalTimeSpent + (userAnswerData?.time_spent || 0)
    }));
    
    // Show feedback
    setCurrentFeedback({
      isCorrect,
      correctAnswer: correctAnswerDisplay,
      rationale: currentQuestion.rationale || 'No rationale available.',
      sourceReference: currentQuestion.source_reference
    });
    setShowFeedback(true);
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setShowFeedback(false);
      setCurrentFeedback(null);
      setQuestionStartTime(Date.now());
    }
  };

  const handleFinishQuiz = async () => {
    setSubmitting(true);
    try {
      // Submit all answers to track progress
      const submissions = Object.entries(answers).map(([question_id, data]) => ({
        question_id,
        selected_answer: data.answer,
        time_spent: data.time_spent
      }));

      const response = await axios.post(
        `${BACKEND_URL}/api/quiz/submit`,
        submissions,
        { withCredentials: true }
      );

      // Navigate to results with comprehensive data
      navigate('/results', {
        state: {
          results: response.data.results,
          meta,
          sessionStats: {
            ...sessionStats,
            totalQuestions: questions.length
          },
          mode
        }
      });
    } catch (error) {
      toast.error('Failed to submit quiz');
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Empty state
  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="bg-white border-b border-slate-200">
          <div className="max-w-3xl mx-auto px-6 py-4">
            <Button
              onClick={() => navigate('/quiz/setup', { replace: true })}
              variant="ghost"
              className="hover:bg-slate-100 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Setup
            </Button>
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-12">
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <AlertCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-slate-900 mb-2">No Questions Available</h2>
            <p className="text-slate-600 mb-6">
              {meta?.message || 'There are no questions matching your selected topic and difficulty.'}
            </p>
            <Button
              onClick={() => navigate('/quiz/setup', { replace: true })}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Try Different Settings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];
  const currentAnswer = answers[currentQuestion?.question_id]?.answer;
  const progress = ((currentIndex + 1) / questions.length) * 100;
  const isLastQuestion = currentIndex === questions.length - 1;
  const modeIcon = mode === 'skill_builder' ? Brain : BookOpen;
  const ModeIcon = modeIcon;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            {/* EXIT QUIZ BUTTON - Uses unified exit handler */}
            <Button
              onClick={handleExitQuiz}
              variant="ghost"
              className="hover:bg-slate-100 text-slate-600 hover:text-slate-900"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Exit Quiz
            </Button>
            
            {/* Session Stats */}
            <div className="flex items-center gap-4 text-sm">
              <span className="text-slate-500">
                Score: <span className="font-semibold text-slate-900">{sessionStats.correctCount}/{sessionStats.questionsAnswered}</span>
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            <ModeIcon className={`w-5 h-5 ${mode === 'skill_builder' ? 'text-accent' : 'text-primary'}`} />
            <h1 className="text-xl font-bold text-slate-900">
              {mode === 'skill_builder' ? 'Skill Builder' : 'Standard Quiz'}
            </h1>
            {meta?.topic && meta.topic !== 'all' && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                {meta.topic}
              </span>
            )}
            {meta?.difficulty && meta.difficulty !== 'all' && (
              <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                meta.difficulty === 'beginner' ? 'bg-emerald-100 text-emerald-700' :
                meta.difficulty === 'intermediate' ? 'bg-amber-100 text-amber-700' :
                'bg-red-100 text-red-700'
              }`}>
                {meta.difficulty}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className={`h-2 ${mode === 'skill_builder' ? 'bg-accent/20' : 'bg-primary/20'}`}>
        <div
          className={`h-full transition-all duration-300 ${mode === 'skill_builder' ? 'bg-accent' : 'bg-primary'}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Progress indicator */}
        <div className="mb-6">
          <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
            <span>Question {currentIndex + 1} of {questions.length}</span>
            <span>{sessionStats.questionsAnswered} completed</span>
          </div>
          <div className="flex gap-1">
            {questions.map((_, idx) => (
              <div
                key={idx}
                className={`h-1.5 flex-1 rounded-full transition-all ${
                  idx < currentIndex ? 'bg-green-500' :
                  idx === currentIndex ? (mode === 'skill_builder' ? 'bg-accent' : 'bg-primary') :
                  'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Question metadata */}
        <div className="flex gap-2 mb-4 flex-wrap">
          <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-bold tracking-wide uppercase">
            {currentQuestion?.topic_display_name || currentQuestion?.topic_id || 'Topic'}
          </span>
          <span className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-bold tracking-wide uppercase">
            {currentQuestion?.subtopic_display_name || currentQuestion?.subtopic || 'Subtopic'}
          </span>
          <span className={`px-3 py-1 text-xs rounded-full font-bold tracking-wide uppercase ${
            currentQuestion?.difficulty_level === 'beginner' ? 'bg-emerald-100 text-emerald-700' :
            currentQuestion?.difficulty_level === 'intermediate' ? 'bg-amber-100 text-amber-700' :
            'bg-red-100 text-red-700'
          }`}>
            {currentQuestion?.difficulty_level || 'intermediate'}
          </span>
        </div>

        {/* Question Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-8 mb-6">
          <h2 className="text-xl md:text-2xl font-semibold text-slate-900 mb-8 leading-relaxed">
            {currentQuestion?.question_text || 'Question text not available'}
          </h2>

          {/* Answer Options */}
          {currentQuestion?.question_type === 'multiple_choice' || currentQuestion?.question_type === 'true_false' ? (
            <div className="space-y-3">
              {(currentQuestion.options || []).map((option, idx) => {
                const isSelected = currentAnswer === option;
                const isCorrectOption = showFeedback && option === currentFeedback?.correctAnswer;
                const isWrongSelected = showFeedback && isSelected && !currentFeedback?.isCorrect;
                
                return (
                  <button
                    key={idx}
                    onClick={() => handleAnswer(option)}
                    disabled={showFeedback}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      showFeedback
                        ? isCorrectOption
                          ? 'border-green-500 bg-green-50'
                          : isWrongSelected
                            ? 'border-red-500 bg-red-50'
                            : 'border-slate-200 bg-slate-50 opacity-60'
                        : isSelected
                          ? 'border-primary bg-primary/5 shadow-sm'
                          : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        showFeedback
                          ? isCorrectOption
                            ? 'border-green-500 bg-green-500'
                            : isWrongSelected
                              ? 'border-red-500 bg-red-500'
                              : 'border-slate-300'
                          : isSelected
                            ? 'border-primary bg-primary'
                            : 'border-slate-300'
                      }`}>
                        {showFeedback && isCorrectOption && <Check className="w-4 h-4 text-white" />}
                        {showFeedback && isWrongSelected && <X className="w-4 h-4 text-white" />}
                        {!showFeedback && isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <span className="text-base text-slate-900">{option}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            // Fill in the blank
            <div>
              <input
                type="text"
                value={currentAnswer || ''}
                onChange={(e) => handleAnswer(e.target.value)}
                disabled={showFeedback}
                placeholder="Type your answer here..."
                className={`w-full h-12 bg-slate-50 border rounded-lg px-4 transition-all text-base ${
                  showFeedback
                    ? currentFeedback?.isCorrect
                      ? 'border-green-500 bg-green-50'
                      : 'border-red-500 bg-red-50'
                    : 'border-slate-200 focus:border-primary focus:ring-2 focus:ring-primary/20'
                }`}
              />
              {showFeedback && !currentFeedback?.isCorrect && currentFeedback?.correctAnswer && (
                <p className="mt-2 text-sm text-green-600">
                  Correct answer: <strong>{currentFeedback.correctAnswer}</strong>
                </p>
              )}
            </div>
          )}
        </div>

        {/* Feedback Panel */}
        {showFeedback && currentFeedback && (
          <div className={`rounded-xl p-6 mb-6 ${
            currentFeedback.isCorrect
              ? 'bg-green-50 border border-green-200'
              : 'bg-red-50 border border-red-200'
          }`}>
            <div className="flex items-center gap-2 mb-3">
              {currentFeedback.isCorrect ? (
                <>
                  <Check className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-green-700">Correct!</span>
                </>
              ) : (
                <>
                  <X className="w-5 h-5 text-red-600" />
                  <span className="font-semibold text-red-700">Incorrect</span>
                </>
              )}
            </div>
            
            <div className="mb-3">
              <h4 className="text-sm font-semibold text-slate-700 mb-1">Rationale</h4>
              <p className="text-sm text-slate-600">{currentFeedback.rationale}</p>
            </div>
            
            {currentFeedback.sourceReference && (
              <div className="text-xs text-slate-500">
                Source: {currentFeedback.sourceReference}
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between items-center">
          <div className="text-sm text-slate-500">
            {sessionStats.questionsAnswered > 0 && (
              <span>
                Accuracy: {Math.round((sessionStats.correctCount / sessionStats.questionsAnswered) * 100)}%
              </span>
            )}
          </div>

          <div className="flex gap-3">
            {!showFeedback ? (
              <Button
                onClick={handleCheckAnswer}
                disabled={!currentAnswer && currentAnswer !== ''}
                className={`h-12 px-8 rounded-full font-semibold shadow-lg transition-all hover:-translate-y-0.5 ${
                  mode === 'skill_builder'
                    ? 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent/20'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/20'
                }`}
              >
                Check Answer
              </Button>
            ) : isLastQuestion ? (
              <Button
                onClick={handleFinishQuiz}
                disabled={submitting}
                className="bg-green-600 text-white hover:bg-green-700 h-12 px-8 rounded-full font-semibold shadow-lg transition-all hover:-translate-y-0.5"
              >
                {submitting ? 'Submitting...' : 'Finish Quiz'}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className={`h-12 px-8 rounded-full font-semibold shadow-lg transition-all hover:-translate-y-0.5 ${
                  mode === 'skill_builder'
                    ? 'bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent/20'
                    : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-primary/20'
                }`}
              >
                Next Question
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
      
      {/* Exit Confirmation Modal */}
      <AlertDialog open={showExitModal} onOpenChange={setShowExitModal}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <LogOut className="w-5 h-5 text-amber-500" />
              Exit Quiz?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Your progress will not be saved. You have answered {sessionStats.questionsAnswered} of {questions.length} questions 
              ({sessionStats.correctCount} correct).
              <br /><br />
              Are you sure you want to exit?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowExitModal(false)}>
              Continue Quiz
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={performExit}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Exit Quiz
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
